import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Dropdown,
  Modal,
  Form,
  Row,
  Col,
  Select,

  Divider,
  Steps,
  List,
  Typography,
  message,
} from 'antd';
import { Tabs } from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  ExportOutlined,
  PrinterOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import useStore from '../store/useStore';
import { ordersAPI, customersAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import FormField from '../components/forms/FormField';
import RightDrawer from '../components/common/RightDrawer';
import DetailView, { DetailSection, DetailField } from '../components/common/DetailView';

const { Search } = Input;
const { Option } = Select;

const { Text } = Typography;

const Orders = () => {
  const { orders, setOrders, customers, setCustomers } = useStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [currentView, setCurrentView] = useState('list'); // 'list' | 'detail'
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [form] = Form.useForm();

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ordersAPI.getAll();
      setOrders(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await customersAPI.getAll();
      setCustomers(response.data);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.id.toString().includes(searchText) ||
      order.customerName?.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleAddOrder = () => {
    setEditingOrder(null);
    form.resetFields();
    setDrawerVisible(true);
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    form.setFieldsValue({
      ...order,
      orderDate: order.orderDate ? moment(order.orderDate) : null,
      dueDate: order.dueDate ? moment(order.dueDate) : null,
    });
    setDrawerVisible(true);
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setCurrentView('detail');
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      await ordersAPI.delete(orderId);
      await fetchOrders();
      message.success('Order deleted successfully');
    } catch (err) {
      message.error('Failed to delete order');
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await ordersAPI.updateStatus(orderId, newStatus);
      await fetchOrders();
      message.success('Order status updated successfully');
    } catch (err) {
      message.error('Failed to update order status');
    }
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setEditingOrder(null);
    form.resetFields();
  };

  const handleDrawerSave = async () => {
    try {
      setDrawerLoading(true);
      const values = await form.validateFields();
      
      if (editingOrder) {
        await ordersAPI.update(editingOrder.id, values);
        message.success('Order updated successfully');
      } else {
        await ordersAPI.create(values);
        message.success('Order created successfully');
      }
      
      await fetchOrders();
      handleDrawerClose();
    } catch (err) {
      message.error('Failed to save order');
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedOrder(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      confirmed: 'blue',
      processing: 'purple',
      shipped: 'cyan',
      delivered: 'green',
      cancelled: 'red',
    };
    return colors[status] || 'default';
  };

  const getStatusStep = (status) => {
    const steps = {
      pending: 0,
      confirmed: 1,
      processing: 2,
      shipped: 3,
      delivered: 4,
    };
    return steps[status] || 0;
  };

  // Calculate line totals, subtotal, and balance
  const calcOrderTotals = (order) => {
    const items = order?.items || [];
    const subtotal = items.reduce((sum, it) => sum + (it.quantity || 0) * (it.price || 0), 0);
    // If order.total exists, prefer it; else use subtotal
    const total = typeof order.total === 'number' ? order.total : subtotal;
    const balance = typeof order.balance === 'number' ? order.balance : total; // placeholder logic
    return { subtotal, total, balance };
  };

  const columns = [
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
      render: (id) => (
        <Text className="font-mono text-sm">#{id}</Text>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (name, record) => (
        <div>
          <div className="font-medium">{name}</div>
          <div className="text-sm text-gray-500">{record.customerEmail}</div>
        </div>
      ),
    },
    {
      title: 'Order Date',
      dataIndex: 'orderDate',
      key: 'orderDate',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <Select
          value={status}
          size="small"
          style={{ width: 120 }}
          onChange={(newStatus) => handleStatusChange(record.id, newStatus)}
        >
          <Option value="pending">Pending</Option>
          <Option value="confirmed">Confirmed</Option>
          <Option value="processing">Processing</Option>
          <Option value="shipped">Shipped</Option>
          <Option value="delivered">Delivered</Option>
          <Option value="cancelled">Cancelled</Option>
        </Select>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (total) => `$${total?.toFixed(2)}`,
      sorter: (a, b) => a.total - b.total,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'view',
                icon: <EyeOutlined />,
                label: 'View Details',
                onClick: () => handleViewOrder(record),
              },
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Edit',
                onClick: () => handleEditOrder(record),
              },
              {
                key: 'print',
                icon: <PrinterOutlined />,
                label: 'Print Invoice',
              },
              {
                type: 'divider',
              },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Delete',
                danger: true,
                onClick: () => {
                  Modal.confirm({
                    title: 'Delete Order',
                    content: 'Are you sure you want to delete this order?',
                    onOk: () => handleDeleteOrder(record.id),
                  });
                },
              },
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  useEffect(() => {
    fetchOrders();
    fetchCustomers();
  }, [fetchOrders, fetchCustomers]);

  if (loading) {
    return <LoadingSpinner size="large" tip="Loading orders..." />;
  }

  if (error) {
    return (
      <ErrorDisplay
        error={error}
        onRetry={fetchOrders}
        title="Failed to load orders"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">Orders</h1>
          <p className="text-gray-600">Manage customer orders and fulfillment</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddOrder}
          size="large"
        >
          Create Order
        </Button>
      </div>

      {/* Filters and Actions */}
      {currentView === 'list' && (
      <Card>
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex gap-4">
            <Search
              placeholder="Search orders by ID or customer..."
              allowClear
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: 300 }}
              enterButton={<SearchOutlined />}
            />
            
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 150 }}
              placeholder="Filter by status"
            >
              <Option value="all">All Status</Option>
              <Option value="pending">Pending</Option>
              <Option value="confirmed">Confirmed</Option>
              <Option value="processing">Processing</Option>
              <Option value="shipped">Shipped</Option>
              <Option value="delivered">Delivered</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </div>
          
          <Space>
            <Button icon={<ExportOutlined />}>Export</Button>
            <Button icon={<PrinterOutlined />}>Print Labels</Button>
          </Space>
        </div>
      </Card>
      )}

      {/* Orders Table / Detail View */}
      <Card>
        {currentView === 'list' ? (
          <Table
            dataSource={filteredOrders}
            columns={columns}
            rowKey="id"
            onRow={(record) => ({
              onClick: () => handleViewOrder(record),
              className: 'cursor-pointer hover:bg-gray-50',
            })}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} orders`,
            }}
            scroll={{ x: 800 }}
          />
        ) : (
          selectedOrder && (
            <DetailView
              title={`Order #${selectedOrder.id}`}
              subtitle={`${selectedOrder.customerName} • ${selectedOrder.customerEmail || ''}`}
              onBack={handleBackToList}
              onEdit={() => handleEditOrder(selectedOrder)}
              onDelete={() => {
                Modal.confirm({
                  title: 'Delete Order',
                  content: 'Are you sure you want to delete this order?',
                  onOk: () => handleDeleteOrder(selectedOrder.id),
                });
              }}
              tags={[{ label: selectedOrder.status?.toUpperCase(), color: getStatusColor(selectedOrder.status) }]}
            >
              <Row gutter={24}>
                <Col xs={24} lg={16} xl={16}>
                  <Tabs
                    defaultActiveKey="status"
                    items={[
                      {
                        key: 'status',
                        label: 'Status',
                        children: (
                          <>
                            <DetailSection title="Status">
                              <Steps
                                current={getStatusStep(selectedOrder.status)}
                                size="small"
                                items={[
                                  { title: 'Pending', icon: <ClockCircleOutlined /> },
                                  { title: 'Confirmed', icon: <CheckCircleOutlined /> },
                                  { title: 'Processing', icon: <ClockCircleOutlined /> },
                                  { title: 'Shipped', icon: <TruckOutlined /> },
                                  { title: 'Delivered', icon: <CheckCircleOutlined /> },
                                ]}
                              />
                            </DetailSection>
                            <DetailSection title="Order Overview">
                              <Row gutter={24}>
                                <Col span={8}>
                                  <DetailField label="Fulfill from" value={selectedOrder.fulfillFrom || 'Not set'} />
                                  <DetailField label="Ordered on" value={selectedOrder.orderDate} render={(v) => v ? new Date(v).toLocaleDateString() : '-'} />
                                  <DetailField label="Assigned to" value={selectedOrder.assignedTo || 'Unassigned'} />
                                  <DetailField label="PO #" value={selectedOrder.poNumber || '-'} />
                                  <DetailField label="Sales rep" value={selectedOrder.salesRep || '-'} />
                                </Col>
                                <Col span={8}>
                                  <DetailField label="Customer" value={selectedOrder.customerName} />
                                  {selectedOrder.customerEmail && (<DetailField label="Email" value={selectedOrder.customerEmail} />)}
                                  {selectedOrder.customerPhone && (<DetailField label="Phone" value={selectedOrder.customerPhone} />)}
                                  <DetailField
                                    label="Billing address"
                                    value={selectedOrder.billingAddress}
                                    render={(addr) => addr ? (
                                      <div>
                                        {addr.name && <div>{addr.name}</div>}
                                        {addr.line1 && <div>{addr.line1}</div>}
                                        {addr.line2 && <div>{addr.line2}</div>}
                                        <div>{[addr.city, addr.state, addr.zip].filter(Boolean).join(', ')}</div>
                                        {addr.country && <div>{addr.country}</div>}
                                      </div>
                                    ) : '-'}
                                  />
                                  <DetailField
                                    label="Shipping address"
                                    value={selectedOrder.shippingAddress}
                                    render={(addr) => addr ? (
                                      <div>
                                        {addr.name && <div>{addr.name}</div>}
                                        {addr.line1 && <div>{addr.line1}</div>}
                                        {addr.line2 && <div>{addr.line2}</div>}
                                        <div>{[addr.city, addr.state, addr.zip].filter(Boolean).join(', ')}</div>
                                        {addr.country && <div>{addr.country}</div>}
                                      </div>
                                    ) : (selectedOrder.shippingSameAsBilling ? 'Same as billing address' : '-')}
                                  />
                                </Col>
                                <Col span={8}>
                                  <DetailField label="Set ship by date" value={selectedOrder.shipByDate} render={(v) => v ? new Date(v).toLocaleDateString() : '-'} />
                                  <DetailField label="Ship order" value={selectedOrder.shipOrder} render={(v) => v ? 'Yes' : 'No'} />
                                  <DetailField label="Same as billing address" value={selectedOrder.shippingSameAsBilling} render={(v) => v ? 'Yes' : 'No'} />
                                </Col>
                              </Row>
                              {selectedOrder.notes && (<DetailField label="Order remarks" value={selectedOrder.notes} />)}
                            </DetailSection>
                          </>
                        )
                      },
                      {
                        key: 'invoices',
                        label: 'Invoices',
                        children: (
                          <DetailSection
                            title="Invoiced"
                            extra={<Tag color="gold">${calcOrderTotals(selectedOrder).total?.toFixed(2)}</Tag>}
                          >
                            <Row gutter={24}>
                              <Col span={12}>
                                <DetailField label="Payment history" value={selectedOrder.paymentHistory || 'No payments yet'} />
                                <DetailField label="Tax region" value={selectedOrder.taxRegion || 'Not set'} />
                                {typeof selectedOrder.taxRate === 'number' && (<DetailField label="Tax rate" value={`${selectedOrder.taxRate}%`} />)}
                              </Col>
                              <Col span={12}>
                                <DetailField label="Level" value={selectedOrder.invoiceLevel || '-'} />
                                <DetailField label="Invoiced on" value={selectedOrder.invoicedOn} render={(v) => v ? new Date(v).toLocaleDateString() : '-'} />
                                <DetailField label="Net terms" value={selectedOrder.netTerms || '-'} />
                                <DetailField label="Due on" value={selectedOrder.dueOn} render={(v) => v ? new Date(v).toLocaleDateString() : '-'} />
                              </Col>
                            </Row>
                            <Space>
                              <Button type="primary">Mark paid</Button>
                              <Button>Fulfill</Button>
                            </Space>
                          </DetailSection>
                        )
                      },
                    ]}
                  />
                </Col>
                <Col xs={24} lg={8} xl={8}>
                  <DetailSection title="Order Items">
                    <List
                      dataSource={selectedOrder.items || []}
                      renderItem={(item) => (
                        <List.Item className="flex justify-between">
                          <div>
                            <div className="font-medium">{item.productName}</div>
                            <div className="text-sm text-gray-500">Quantity: {item.quantity} × ${item.price}</div>
                          </div>
                          <div className="font-medium">${(item.quantity * item.price).toFixed(2)}</div>
                        </List.Item>
                      )}
                      locale={{ emptyText: 'No items' }}
                    />
                    <Divider />
                    <div className="flex justify-end">
                      <div className="text-right">
                        <div className="text-base">Subtotal: ${calcOrderTotals(selectedOrder).subtotal.toFixed(2)}</div>
                        <div className="text-lg font-semibold">Balance: ${calcOrderTotals(selectedOrder).balance.toFixed(2)}</div>
                      </div>
                    </div>
                  </DetailSection>
                </Col>
              </Row>
            </DetailView>
          )
        )}
      </Card>

      {/* Right Drawer for Add/Edit */}
      <RightDrawer
        title={editingOrder ? 'Edit Order' : 'Create New Order'}
        open={drawerVisible}
        onClose={handleDrawerClose}
        loading={drawerLoading}
        width={800}
      >
        <Form form={form} layout="vertical" className="mt-2" onFinish={handleDrawerSave} id="drawer-form">
          <Row gutter={16}>
            <Col span={12}>
              <FormField
                name="customerId"
                label="Customer"
                type="select"
                required
                options={customers.map(customer => ({
                  value: customer.id,
                  label: customer.name,
                }))}
                placeholder="Select customer"
              />
            </Col>
            <Col span={12}>
              <FormField
                name="status"
                label="Status"
                type="select"
                required
                options={[
                  { value: 'pending', label: 'Pending' },
                  { value: 'confirmed', label: 'Confirmed' },
                  { value: 'processing', label: 'Processing' },
                  { value: 'shipped', label: 'Shipped' },
                  { value: 'delivered', label: 'Delivered' },
                ]}
                placeholder="Select status"
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <FormField
                name="orderDate"
                label="Order Date"
                type="date"
                required
              />
            </Col>
            <Col span={12}>
              <FormField
                name="dueDate"
                label="Due Date"
                type="date"
              />
            </Col>
          </Row>

          <FormField
            name="notes"
            label="Notes"
            type="textarea"
            placeholder="Order notes or special instructions"
          />
        </Form>
      </RightDrawer>
    </div>
  );
};

export default Orders;
